import { useEffect, useMemo, useState } from 'react';
import { useRefillsStore } from '../../../stores/useRefillsStore';
import { usePersonalInfoStore } from '../../../stores/usePersonalInfoStore';
import { getSessionUser, type SessionUser } from '../../../utils/session';
import { getAddressTypes } from '../../../services/listsApi';
import type { DeliveryType, PickupLocation, PaymentType } from '../../../stores/types';

interface AddressOption {
  AddressTypeId: number;
  AddressTypeName: string;
}

interface StreetAddress {
  AddressTypeId?: number;
  Street1?: string;
  Street2?: string;
  City?: string;
  State?: string;
  Zip?: string;
}

export interface DeliveryOptionsProps {
  loading: boolean;
}

const MAIL_ORDER_ID = 10001;

export function DeliveryOptions({ loading }: DeliveryOptionsProps) {
  const {
    availableDeliveryTypes,
    deliveryMethod,
    availableLocations,
    pickupLocation,
    availablePayments,
    paymentType,
    isMailOrder,
    shippingAddressId,
    linkToken,
    setDeliveryMethod,
    setPickupLocation,
    setPaymentType,
    setShippingAddressId,
    loadRefills,
  } = useRefillsStore();

  const [addresses, setAddresses] = useState<AddressOption[]>([]);
  // Re-read the session whenever a Personal Info save completes — otherwise a newly
  // added address type (e.g. selecting "School" for the first time, saving it) never
  // shows up here, since this component's own session snapshot was taken once on
  // mount and Personal Info lives in a separate store with no other link to this one
  // (VSDP-5975).
  const personalInfoSuccessMessage = usePersonalInfoStore(state => state.successMessage);
  const user = useMemo(() => getSessionUser<SessionUser>(), [personalInfoSuccessMessage]);
  const streetAddresses = useMemo(() => {
    const raw = (user?.StreetAddresses as StreetAddress[] | undefined) || [];
    return raw;
  }, [user]);

  const selectedShipping = useMemo(
    () => streetAddresses.find(a => a.AddressTypeId === shippingAddressId) || null,
    [streetAddresses, shippingAddressId]
  );

  useEffect(() => {
    getAddressTypes()
      .then(result => {
        if (result.StatusCode === 'SUCCESS' && Array.isArray(result.AddressTypes)) {
          const mapped = result.AddressTypes.map(a => ({
            // API may return AddressTypeID (capital ID) or AddressTypeId; accept either.
            AddressTypeId: Number(a.AddressTypeID ?? a.AddressTypeId ?? 0),
            AddressTypeName: a.AddressTypeName === 'Account' ? 'Home' : a.AddressTypeName || '',
          })).filter(a => a.AddressTypeId > 0);
          setAddresses(mapped);
        }
      })
      .catch(() => {});
  }, []);

  const handleDeliveryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    const method = availableDeliveryTypes.find(dt => dt.DeliveryTypeId === id);
    if (method) {
      setDeliveryMethod(method);
      if (linkToken) {
        loadRefills(linkToken, method.DeliveryTypeId);
      }
    }
  };

  const handleLocationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    const loc = availableLocations.find(l => l.PickupLocationId === id);
    if (loc) {
      setPickupLocation(loc);
      if (linkToken) {
        loadRefills(linkToken, deliveryMethod?.DeliveryTypeId, loc.PickupLocationId);
      }
    }
  };

  const handlePaymentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    const pay = availablePayments.find(p => p.PaymentTypeId === id);
    if (pay) setPaymentType(pay);
  };

  const handleShippingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    setShippingAddressId(id || null);
  };

  return (
    <>
      {availableDeliveryTypes.length > 1 && (
        <li className="list-group-item">
          <strong>Delivery Method: </strong>
          <br />
          <select
            className="form-control custom"
            value={deliveryMethod?.DeliveryTypeId || ''}
            onChange={handleDeliveryChange}
            required
          >
            {availableDeliveryTypes.map((dt: DeliveryType) => (
              <option key={dt.DeliveryTypeId} value={dt.DeliveryTypeId}>
                {dt.DeliveryTypeName}
              </option>
            ))}
          </select>
        </li>
      )}

      {availableLocations.length > 1 && !loading && (
        <li className="list-group-item">
          <strong>Pickup Location: </strong>
          <br />
          <select
            className="form-control custom"
            style={{ fontSize: '11px' }}
            value={pickupLocation?.PickupLocationId || ''}
            onChange={handleLocationChange}
            required
          >
            {availableLocations.map((loc: PickupLocation) => (
              <option key={loc.PickupLocationId} value={loc.PickupLocationId}>
                {loc.PickupLocationName}
              </option>
            ))}
          </select>
        </li>
      )}

      {availablePayments.length > 1 && (
        <li className="list-group-item">
          <strong>Payment Method: </strong>
          <br />
          <select
            className="form-control custom"
            value={paymentType?.PaymentTypeId || ''}
            onChange={handlePaymentChange}
            required
          >
            {availablePayments.map((pay: PaymentType) => (
              <option key={pay.PaymentTypeId} value={pay.PaymentTypeId}>
                {pay.PaymentTypeName}
              </option>
            ))}
          </select>
        </li>
      )}

      {isMailOrder && !loading && streetAddresses.length > 0 && (
        <li className="list-group-item">
          <strong>Shipping Address:</strong>
          <select
            className="form-control custom"
            value={shippingAddressId || ''}
            onChange={handleShippingChange}
            required
          >
            <option value="">Choose Address</option>
            {addresses.map(addr => (
              <option key={addr.AddressTypeId} value={addr.AddressTypeId}>
                {addr.AddressTypeName}
              </option>
            ))}
          </select>
        </li>
      )}

      {selectedShipping && isMailOrder && (
        <div className="col-lg-12 col-md-12 col-sm-12 shippingaddy">
          <h5 style={{ borderBottom: '1px gainsboro solid' }}>Shipping Address</h5>
          <p>
            {selectedShipping.Street1}
            <br />
            {selectedShipping.Street2 && (
              <>
                {selectedShipping.Street2}
                <br />
              </>
            )}
            {selectedShipping.City} {selectedShipping.State}
            <br />
            {selectedShipping.Zip}
            <br />
          </p>
        </div>
      )}
    </>
  );
}
